import React, { useContext, useState } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { BookmarkAdd, BookmarkAdded, Favorite, FavoriteBorder, PlayCircle, PauseCircle, } from '@mui/icons-material';
import TvOffIcon from '@mui/icons-material/TvOff';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db, auth } from '../firebaseConfig';
import { UserContext } from '../context/UserContext'; // Import du UserContext

function KdramaCard({ id, title, overview, year, images = [], genres = [], rating }) {
    const { userData, setUserData } = useContext(UserContext); // Utilisation du contexte utilisateur
    const truncatedOverview = overview.length > 300 ? overview.slice(0, 297) + '...' : overview;
    const navigate = useNavigate();
    const releaseYear = new Date(year).getFullYear();
    const posterUrl = images.length > 0 ? `https://image.tmdb.org/t/p/w500${images[0].file_path}` : null;

    // Vérifier si le drama est dans les différentes listes de l'utilisateur depuis le contexte
    const isInWatched = userData?.watchedKdramas?.some(drama => String(drama.id) === String(id));
    const isInProgress = userData?.inProgressKdramas?.some(drama => String(drama.id) === String(id));
    const isInToWatch = userData?.toWatchKdramas?.some(drama => String(drama.id) === String(id));
    const isInToFavorite = userData?.favoriteKdramas?.some(drama => String(drama.id) === String(id));
    const [hoverInProgress, setHoverInProgress] = useState(false);
    const [hoverToWatch, setHoverToWatch] = useState(false);
    const [hoverWatched, setHoverWatched] = useState(false);
    const [hoverFavorite, setHoverFavorite] = useState(false);

    // Snackbar pour affichage de messages utilisateur
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');

    const handleClick = () => {
        navigate(`/kdrama/${id}`);
    };

    // Fonction pour mettre à jour la liste dans Firestore et le contexte utilisateur
    const updateUserList = async (list, action, state, successMessage) => {
        const user = auth.currentUser;
        if (!user) return;

        const userRef = doc(db, 'users', user.uid);
        const dramaData = {
            id: id,
            title: title,
            posterUrl: posterUrl,
            state: state
        };

        const actions = {
            add: arrayUnion(dramaData),
            remove: arrayRemove(dramaData),
        };

        try {
            await updateDoc(userRef, {
                [list]: action === 'add' ? actions.add : actions.remove
            });

            // Mettre à jour les données utilisateur dans le contexte
            setUserData(prevState => ({
                ...prevState,
                [list]: action === 'add' ? [...prevState[list], dramaData] : prevState[list].filter(drama => drama.id !== id)
            }));

            // Mise à jour du message et ouverture du Snackbar
            setMessage(successMessage);
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erreur lors de la mise à jour des listes :', error);
        }
    };

    // Fonctions pour gérer les interactions avec les listes
    const toggleFavorite = () => {
        if (isInToFavorite) {
            updateUserList('favoriteKdramas', 'remove', 'toFavorite', 'Retiré de la liste des dramas favoris');
        } else {
            updateUserList('favoriteKdramas', 'add', 'toFavorite', 'Ajouté à la liste des dramas favoris');
        }
    };


    const toggleWatched = () => {
        if (isInWatched) {
            updateUserList('watchedKdramas', 'remove', 'watched', 'Retiré de la liste des dramas vus');
        } else {
            updateUserList('watchedKdramas', 'add', 'watched', 'Ajouté à la liste des dramas vus');
        }
    };

    const toggleInProgress = () => {
        if (isInProgress) {
            updateUserList('inProgressKdramas', 'remove', 'inProgress', 'Retiré de la liste des dramas en cours');
        } else {
            updateUserList('inProgressKdramas', 'add', 'inProgress', 'Ajouté à la liste des dramas en cours');
        }
    };

    const toggleToWatch = () => {
        if (isInToWatch) {
            updateUserList('toWatchKdramas', 'remove', 'toWatch', 'Retiré de la liste des dramas à voir');
        } else {
            updateUserList('toWatchKdramas', 'add', 'toWatch', 'Ajouté à la liste des dramas à voir');
        }
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    return (
        <>
            <Card sx={{
                width: 400,
                height: 500, // Hauteur fixe de la carte
                margin: '1rem',
                borderRadius: '10px',
                backgroundColor: '#f5f5f5',
                transition: 'transform 0.3s',
                display: 'flex',
                flexDirection: 'column', // Alignement vertical
                justifyContent: 'space-between',
                '&:hover': { transform: 'scale(1.05)' },
                cursor: 'pointer'
            }} >
                <Box sx={{
                    height: 250, // Hauteur fixe pour l'image
                    overflow: 'hidden',
                    borderRadius: '10px 10px 0 0'
                }}>
                    <Slider sx={{ height: '100%' }} dots={true} infinite={true} speed={500} slidesToShow={1} slidesToScroll={1}>
                        {images.map((image, index) => (
                            <div key={index}>
                                <img
                                    src={`https://image.tmdb.org/t/p/w500${image.file_path}`}
                                    alt={`Slide ${index + 1}`}
                                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '10px 10px 0 0' }}
                                />
                            </div>
                        ))}
                    </Slider>
                </Box>
                <CardContent onClick={handleClick} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography gutterBottom variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'SUSE, sans-serif' }}>
                            {title} <span style={{ fontFamily: 'SUSE, sans-serif' }}>({releaseYear})</span>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'SUSE, sans-serif' }}>
                            {genres.join(', ')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'justify', marginTop: '1rem', fontFamily: 'SUSE, sans-serif' }}>
                            {truncatedOverview}
                        </Typography>
                    </Box>
                </CardContent>

                {/* Icons for managing lists */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        paddingBottom: '1rem',
                        gap: 2,
                    }}>
                    <Tooltip title="Favoris">
                        <IconButton
                            onMouseEnter={() => setHoverFavorite(true)}
                            onMouseLeave={() => setHoverFavorite(false)}
                            onClick={toggleFavorite}
                            sx={{
                                color: isInToFavorite ? '#E91E63' : '#888888',
                                "&:hover": { color: '#FF4081' }
                            }}
                        >
                            {/* Affiche l'icône Favorite si déjà "vu", sinon FavoriteBorder */}
                            {isInToFavorite ? (
                                <Favorite />
                            ) : (
                                hoverFavorite ? (
                                    <Favorite />
                                ) : (
                                    <FavoriteBorder />
                                )
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Vu">
                        <IconButton
                            onMouseEnter={() => setHoverWatched(true)}
                            onMouseLeave={() => setHoverWatched(false)}
                            onClick={toggleWatched}
                            sx={{
                                color: isInWatched ? '#1976D2' : '#888888',
                                "&:hover": { color: '#42A5F5' }
                            }}
                        >
                            {/* Affiche l'icône Favorite si déjà "vu", sinon FavoriteBorder */}
                            {isInWatched ? (
                                <LiveTvIcon />
                            ) : (
                                hoverWatched ? (
                                    <LiveTvIcon />
                                ) : (
                                    <TvOffIcon />
                                )
                            )}
                        </IconButton>
                    </Tooltip>



                    <Tooltip title="En train de regarder">
                        <IconButton onMouseEnter={() => setHoverInProgress(true)} onMouseLeave={() => setHoverInProgress(false)} onClick={toggleInProgress} sx={{ color: isInProgress ? '#FFA000' : '#888888', "&:hover": { color: '#FFCA28' } }}>
                            {isInProgress ? (
                                <PlayCircle />
                            ) : (
                                hoverInProgress ? (
                                    <PlayCircle />
                                ) : (
                                    <PauseCircle />
                                )
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="À voir">
                        <IconButton
                            onClick={toggleToWatch}
                            sx={{
                                color: isInToWatch ? '#388E3C' : '#888888',
                                "&:hover": { color: '#66BB6A' }
                            }}
                            onMouseEnter={() => setHoverToWatch(true)}
                            onMouseLeave={() => setHoverToWatch(false)}
                        >
                            {/* Si l'utilisateur survole et que l'élément n'est pas encore dans la liste, on affiche BookmarkAdded */}
                            {isInToWatch ? (
                                <BookmarkAdded />
                            ) : (
                                // Sinon on affiche BookmarkAdd (ou BookmarkAdded si déjà dans la liste)
                                hoverToWatch ? (
                                    <BookmarkAdded />
                                ) : (
                                    <BookmarkAdd />
                                )
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Card>
            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%', fontFamily: 'SUSE, sans-serif', }}>
                    {message}
                </Alert>
            </Snackbar>
        </>
    );
}

export default KdramaCard;
